<template>
  <div class="claim-inquiry">
    <section class="inquiry-search-card">
      <div class="inquiry-heading">
        <div class="inquiry-heading-icon">
          <v-icon>mdi-file-search-outline</v-icon>
        </div>
        <div>
          <h2>Cek Klaim</h2>
          <p>Masukkan ID klaim untuk melihat status klaim.</p>
        </div>
      </div>

      <v-form
        ref="form"
        v-model="valid"
        class="inquiry-form"
        @submit.prevent="SearchClaim"
      >
        <v-text-field
          v-model.trim="claimId"
          :rules="claimIdRules"
          label="ID Klaim"
          placeholder="Contoh: CLM-000001"
          outlined
          dense
          hide-details="auto"
          :disabled="loading"
          autocomplete="off"
          clearable
          @keydown.enter.prevent="SearchClaim"
        ></v-text-field>
        <v-btn
          class="inquiry-search-btn"
          depressed
          :loading="loading"
          :disabled="loading"
          type="submit"
        >
          <v-icon left>mdi-magnify</v-icon>
          Cek Klaim
        </v-btn>
      </v-form>
    </section>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="#147a70"
    ></v-progress-linear>

    <section v-if="searched && !loading && !claim" class="inquiry-empty-state">
      <v-icon>mdi-file-alert-outline</v-icon>
      <h3>Data klaim tidak ditemukan</h3>
      <p>Periksa kembali ID klaim yang dimasukkan lalu coba lagi.</p>
    </section>

    <template v-if="claim && !loading">
      <section class="claim-progress-card" aria-label="Progres status klaim">
        <div class="claim-progress-heading">
          <div>
            <span>Progres Klaim</span>
            <small>Pantau tahapan penyelesaian klaim</small>
          </div>
          <v-chip class="claim-status-chip" small>{{
            claimStatusText
          }}</v-chip>
        </div>
        <div class="claim-progress-scroll">
          <div class="claim-progress-track">
            <div
              v-for="(step, index) in progressSteps"
              :key="step"
              class="claim-progress-step"
              :class="{
                'is-complete': IsProgressComplete(index),
                'is-active': IsProgressActive(index),
                'is-future': index > progressIndex
              }"
            >
              <div class="claim-step-marker">
                <v-icon v-if="IsProgressComplete(index)" small>
                  mdi-check
                </v-icon>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <div class="claim-step-label">{{ step }}</div>
              <div class="claim-step-meta">
                {{ ProgressText(index) }}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="claim-info-card">
        <div class="claim-section-heading">
          <div>
            <span>Detail Klaim</span>
            <small>Informasi utama klaim</small>
          </div>
          <strong>{{ claim.claim_id }}</strong>
        </div>
        <div class="claim-detail-list">
          <div
            v-for="item in detailItems"
            :key="item.label"
            class="claim-detail-row"
          >
            <span>{{ item.label }}</span>
            <strong>{{ item.value || "-" }}</strong>
          </div>
        </div>
      </section>

      <div class="inquiry-secondary-grid">
        <section class="claim-info-card secondary-card">
          <div class="claim-section-heading">
            <div>
              <span>Dokumen Klaim</span>
              <small>{{ documents.length }} dokumen tersedia</small>
            </div>
            <v-icon>mdi-folder-text-outline</v-icon>
          </div>
          <v-progress-linear
            v-if="loadingDocuments"
            indeterminate
            color="#147a70"
          ></v-progress-linear>
          <div v-if="documents.length" class="document-list">
            <div
              v-for="(document, index) in documents"
              :key="
                document.attachment_row || document.attachment_path || index
              "
              class="document-row"
            >
              <v-icon>{{ AttachmentIcon(document.attachment_path) }}</v-icon>
              <div>
                <strong>{{ AttachmentName(document.attachment_path) }}</strong>
                <small>Dokumen pendukung klaim</small>
              </div>
              <v-btn
                :href="document.attachment_path_url"
                target="_blank"
                icon
                small
                aria-label="Unduh dokumen"
              >
                <v-icon small>mdi-download-outline</v-icon>
              </v-btn>
            </div>
          </div>
          <div v-else-if="!loadingDocuments" class="secondary-empty">
            <v-icon>mdi-file-document-outline</v-icon>
            <span>Belum ada dokumen klaim.</span>
          </div>
        </section>

        <section class="claim-info-card secondary-card">
          <div class="claim-section-heading">
            <div>
              <span>Subrogasi</span>
              <small>Informasi pemulihan klaim</small>
            </div>
            <v-icon>mdi-cash-multiple</v-icon>
          </div>
          <v-data-table
            :headers="subrogationHeaders"
            :items="subrogations"
            :loading="loadingSubrogations"
            loading-text="Memuat data subrogasi..."
            no-data-text="Belum ada data subrogasi"
            item-key="claimsubro_row"
            class="inquiry-subrogation-table"
            hide-default-footer
            dense
          >
            <template v-slot:item.claimsubro_refdate="{ item }">
              {{ FormatDate(item.claimsubro_refdate) }}
            </template>
            <template v-slot:item.claimsubro_amount="{ item }">
              {{ FormatAmount(item.claimsubro_amount, item.claimsubro_ccy) }}
            </template>
            <template v-slot:item.claimsubro_status="{ item }">
              {{ item.claimsubro_statusdesc || item.claimsubro_status || "-" }}
            </template>
          </v-data-table>
        </section>
      </div>
    </template>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      rounded="pill"
      bottom
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Tutup</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: "AppPgdInquiry",
  data: () => ({
    valid: false,
    loading: false,
    searched: false,
    claimId: "",
    claim: null,
    documents: [],
    subrogations: [],
    loadingDocuments: false,
    loadingSubrogations: false,
    claimIdRules: [
      value => Boolean(String(value || "").trim()) || "ID klaim wajib diisi"
    ],
    subrogationHeaders: [
      { text: "Keterangan", value: "claimsubro_description" },
      { text: "Tanggal", value: "claimsubro_refdate" },
      { text: "No. Referensi", value: "claimsubro_refno" },
      { text: "Nilai", value: "claimsubro_amount" },
      { text: "Status", value: "claimsubro_status" }
    ],
    snackbar: { show: false, color: "error", text: "" }
  }),
  computed: {
    progressSteps() {
      const finalStatus = ["CLAIM PAID", "CANCEL", "CLOSE"].includes(
        this.claimStatusText
      )
        ? this.claimStatusText
        : "CLAIM PAID";

      return [
        "CLAIM REGISTER AND VERIFICATION",
        "WAITING DOCUMENT",
        "PENDING PAYMENT",
        finalStatus
      ];
    },
    detailItems() {
      if (!this.claim) return [];
      return [
        { label: "No. Klaim", value: this.claim.claim_id },
        {
          label: "Jenis / Produk",
          value: this.claim.claim_clientproductname || this.claim.claim_category
        },
        { label: "No. Polis", value: this.claim.claim_refid },
        { label: "Objek Klaim", value: this.claim.claim_vehno },
        {
          label: "Tanggal Kejadian",
          value: this.FormatDate(this.claim.claim_dol)
        },
        {
          label: "Tanggal Laporan",
          value: this.FormatDate(this.claim.claim_reportdate)
        },
        { label: "Outlet", value: this.claim.claim_branchclientname },
        {
          label: "Area / Kanwil",
          value: [
            this.claim.claim_branchclientarea,
            this.claim.claim_branchclientkanwil
          ]
            .filter(Boolean)
            .join(" / ")
        },
        { label: "Kronologi", value: this.claim.claim_description },
        { label: "Status", value: this.claimStatusText }
      ];
    },
    claimStatusText() {
      const status = String(
        (this.claim && this.claim.claim_status) || ""
      )
        .trim()
        .toUpperCase();
      const statusGroups = {
        "CLAIM REGISTER AND VERIFICATION": [
          "B",
          "U",
          "R",
          "G",
          "A",
          "S",
          "M",
          "I",
          "V",
          "K",
          "H"
        ],
        "WAITING DOCUMENT": ["N"],
        "PENDING PAYMENT": ["T", "D", "P", "O", "E"],
        "CLAIM PAID": ["Y"],
        CANCEL: ["C"],
        CLOSE: ["X", "W", "F"],
        PENDING: ["L"]
      };
      const mappedStatus = Object.keys(statusGroups).find(label =>
        statusGroups[label].includes(status)
      );

      return mappedStatus || status || "Menunggu Data";
    },
    progressIndex() {
      const statusSteps = {
        "CLAIM REGISTER AND VERIFICATION": 0,
        "WAITING DOCUMENT": 1,
        PENDING: 2,
        "PENDING PAYMENT": 2,
        "CLAIM PAID": 3,
        CANCEL: 3,
        CLOSE: 3
      };

      return statusSteps[this.claimStatusText] || 0;
    }
  },
  methods: {
    AttachmentName(path) {
      if (!path) return "Dokumen tanpa nama";
      return String(path)
        .split(/[\\/]/)
        .pop();
    },
    AttachmentIcon(path) {
      const extension = String(path || "")
        .split(".")
        .pop()
        .toLowerCase();
      if (extension === "pdf") return "mdi-file-pdf-box";
      if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension))
        return "mdi-file-image-outline";
      if (["xls", "xlsx", "csv"].includes(extension))
        return "mdi-file-excel-outline";
      return "mdi-file-document-outline";
    },
    FormatAmount(value, currency) {
      const amount = Number(value || 0).toLocaleString("id-ID");
      return `${currency || "IDR"} ${amount}`;
    },
    IsProgressComplete(index) {
      const isTerminal = ["CLAIM PAID", "CANCEL", "CLOSE"].includes(
        this.claimStatusText
      );
      return (
        index < this.progressIndex ||
        (isTerminal && index === this.progressIndex)
      );
    },
    IsProgressActive(index) {
      const isTerminal = ["CLAIM PAID", "CANCEL", "CLOSE"].includes(
        this.claimStatusText
      );
      return (
        index === this.progressIndex && !isTerminal
      );
    },
    ProgressText(index) {
      if (this.IsProgressComplete(index)) return "Selesai";
      if (this.IsProgressActive(index)) return "Sedang diproses";
      return "Menunggu";
    },
    FormatDate(value) {
      return value ? this.$functions.FormatDateIndo(value) : "";
    },
    SearchClaim() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      this.searched = false;
      this.claim = null;
      this.documents = [];
      this.subrogations = [];
      const formData = new FormData();
      formData.append("claim_id", this.claimId.trim());
      formData.append(
        "login_user",
        localStorage.getItem("website_users_id") || ""
      );
      formData.append("limit", 1);
      this.$axios
        .post(this.$functions.SafeURL("apiListClaimPegadaian", ""), formData, {
          headers: { "Content-Type": "multipart/form-data" }
        })
        .then(response => {
          const data = Array.isArray(response.data) ? response.data : [];
          if (data.length && data[0].feedback === "Y") {
            this.claim = data[0];
            this.LoadDocuments(data[0].claim_id);
            this.LoadSubrogations(data[0].claim_row);
          } else if (data.length && data[0].feedback) {
            this.ShowError(data[0].feedback);
          }
        })
        .catch(error =>
          this.ShowError(error.message || "Gagal mengambil data klaim")
        )
        .finally(() => {
          this.loading = false;
          this.searched = true;
        });
    },
    LoadDocuments(claimId) {
      this.loadingDocuments = true;
      const formData = new FormData();
      formData.append("attachment_claimrow", claimId);
      formData.append("attachment_modul", "claimreg");
      this.$axios
        .post(this.$functions.SafeURL("apiListAttachment", ""), formData, {
          headers: { "Content-Type": "multipart/form-data" }
        })
        .then(response => {
          const data = Array.isArray(response.data) ? response.data : [];
          this.documents = data.filter(document => document.attachment_path);
        })
        .catch(error =>
          this.ShowError(error.message || "Gagal mengambil dokumen klaim")
        )
        .finally(() => {
          this.loadingDocuments = false;
        });
    },
    LoadSubrogations(claimRow) {
      if (!claimRow) return;
      this.loadingSubrogations = true;
      const formData = new FormData();
      formData.append("claimsubro_claimrow", claimRow);
      formData.append(
        "login_user",
        localStorage.getItem("website_users_id") || ""
      );
      formData.append("order_by", "claimsubro_refdate");
      formData.append("order_type", "DESC");
      this.$axios
        .post(this.$functions.SafeURL("apiListClaimSubro", ""), formData, {
          headers: { "Content-Type": "multipart/form-data" }
        })
        .then(response => {
          const data = Array.isArray(response.data) ? response.data : [];
          this.subrogations =
            data.length && data[0].feedback === "Y" ? data : [];
        })
        .catch(error =>
          this.ShowError(error.message || "Gagal mengambil data subrogasi")
        )
        .finally(() => {
          this.loadingSubrogations = false;
        });
    },
    ShowError(message) {
      this.snackbar.text = message;
      this.snackbar.show = true;
    }
  }
};
</script>

<style scoped>
.claim-inquiry {
  position: relative;
  overflow: hidden;
  --claim-ink: #102348;
  --claim-muted: #63728d;
  --claim-teal: #087b72;
  padding: 34px 28px 46px;
  min-height: 520px;
  background: radial-gradient(
      circle at 8% 5%,
      rgba(109, 211, 201, 0.2),
      transparent 28%
    ),
    radial-gradient(circle at 92% 2%, rgba(137, 169, 238, 0.2), transparent 31%),
    linear-gradient(145deg, #f5f9fb 0%, #edf4f7 52%, #f7f9fc 100%);
  isolation: isolate;
}
.claim-inquiry::before,
.claim-inquiry::after {
  position: absolute;
  z-index: -1;
  border-radius: 50%;
  filter: blur(2px);
  content: "";
  pointer-events: none;
}
.claim-inquiry::before {
  top: 92px;
  right: -100px;
  width: 280px;
  height: 280px;
  background: rgba(80, 173, 210, 0.13);
}
.claim-inquiry::after {
  bottom: -130px;
  left: -90px;
  width: 300px;
  height: 300px;
  background: rgba(40, 181, 147, 0.12);
}
.inquiry-search-card,
.claim-progress-card,
.claim-info-card,
.inquiry-empty-state {
  position: relative;
  max-width: 1080px;
  margin-right: auto;
  margin-left: auto;
  border: 1px solid rgba(255, 255, 255, 0.78);
  border-radius: 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.78),
    rgba(255, 255, 255, 0.5)
  );
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -1px 0 rgba(80, 105, 140, 0.08), 0 18px 48px rgba(35, 62, 89, 0.11),
    0 3px 10px rgba(35, 62, 89, 0.05);
  backdrop-filter: blur(24px) saturate(150%);
  -webkit-backdrop-filter: blur(24px) saturate(150%);
}
.inquiry-search-card {
  overflow: hidden;
  padding: 25px 28px 26px;
}
.inquiry-search-card::before {
  position: absolute;
  top: 0;
  left: 8%;
  width: 62%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.98),
    transparent
  );
  content: "";
}
.inquiry-heading {
  display: flex;
  align-items: center;
  gap: 13px;
}
.inquiry-heading-icon {
  display: grid;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.82);
  border-radius: 16px;
  background: linear-gradient(
    145deg,
    rgba(217, 245, 241, 0.94),
    rgba(186, 229, 224, 0.68)
  );
  box-shadow: inset 0 1px 1px #fff, 0 8px 20px rgba(8, 123, 114, 0.12);
}
.inquiry-heading-icon .v-icon {
  color: var(--claim-teal);
}
.inquiry-heading h2 {
  margin: 0;
  color: var(--claim-ink);
  font-size: 20px;
  font-weight: 750;
  letter-spacing: -0.025em;
}
.inquiry-heading p {
  margin: 2px 0 0;
  color: var(--claim-muted);
  font-size: 12px;
}
.inquiry-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 8px;
  margin-top: 22px;
  padding: 6px;
  border: 1px solid rgba(255, 255, 255, 0.86);
  border-radius: 21px;
  background: rgba(230, 237, 245, 0.42);
  box-shadow: inset 0 1px 3px rgba(35, 62, 89, 0.07),
    0 1px 0 rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}
::v-deep .inquiry-form .v-text-field--outlined .v-input__slot {
  min-height: 48px !important;
  margin-bottom: 0;
  border-radius: 15px !important;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.9),
    0 2px 7px rgba(45, 67, 95, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: background 0.25s ease, box-shadow 0.25s ease;
}
::v-deep .inquiry-form .v-text-field--outlined fieldset {
  border-color: rgba(121, 142, 169, 0.2);
  transition: border-color 0.25s ease;
}
::v-deep
  .inquiry-form
  .v-text-field--outlined.v-input--is-focused
  .v-input__slot {
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 0 0 4px rgba(8, 123, 114, 0.1), inset 0 1px 1px #fff;
}
::v-deep .inquiry-form .v-text-field--outlined.v-input--is-focused fieldset {
  border-width: 1px;
  border-color: rgba(8, 123, 114, 0.82);
}
::v-deep .inquiry-form .v-text-field__slot input {
  color: var(--claim-ink);
  font-weight: 600;
  letter-spacing: 0.015em;
}
.inquiry-search-btn {
  position: relative;
  overflow: hidden;
  height: 48px !important;
  min-width: 166px !important;
  padding: 0 27px !important;
  border: 1px solid rgba(255, 255, 255, 0.76) !important;
  border-radius: 16px !important;
  background: linear-gradient(
    145deg,
    rgba(242, 246, 251, 0.96) 0%,
    rgba(222, 229, 238, 0.94) 100%
  ) !important;
  color: #3191e8 !important;
  text-transform: none !important;
  letter-spacing: 0.01em;
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.98),
    inset 0 -1px 2px rgba(102, 122, 148, 0.1), 0 7px 16px rgba(70, 91, 119, 0.2),
    0 2px 5px rgba(70, 91, 119, 0.1) !important;
  backdrop-filter: blur(16px) saturate(140%);
  -webkit-backdrop-filter: blur(16px) saturate(140%);
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.22s ease, filter 0.22s ease !important;
}
.inquiry-search-btn::before {
  position: absolute;
  top: 1px;
  left: 8%;
  width: 84%;
  height: 48%;
  border-radius: 999px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.78),
    rgba(255, 255, 255, 0.04)
  );
  content: "";
  pointer-events: none;
}
.inquiry-search-btn::after {
  position: absolute;
  top: -80%;
  left: -45%;
  width: 38%;
  height: 250%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.68),
    transparent
  );
  content: "";
  transform: rotate(20deg);
  transition: left 0.55s ease;
  pointer-events: none;
}
.inquiry-search-btn:hover {
  filter: brightness(1.025);
  transform: translateY(-2px) scale(1.012);
  box-shadow: inset 0 1px 1px #fff, inset 0 -1px 2px rgba(102, 122, 148, 0.08),
    0 15px 28px rgba(70, 91, 119, 0.28), 0 4px 8px rgba(70, 91, 119, 0.14) !important;
}
.inquiry-search-btn:hover::after {
  left: 120%;
}
.inquiry-search-btn:active {
  transform: translateY(1px) scale(0.985);
  box-shadow: inset 0 2px 5px rgba(71, 94, 123, 0.18),
    0 5px 11px rgba(70, 91, 119, 0.18) !important;
}
::v-deep .inquiry-search-btn .v-btn__content {
  position: relative;
  z-index: 1;
  font-weight: 700;
}
::v-deep .inquiry-search-btn .v-icon {
  color: #3191e8 !important;
  filter: drop-shadow(0 1px 1px rgba(255, 255, 255, 0.8));
}
.claim-progress-card {
  margin-top: 20px;
  padding: 20px;
}
.claim-progress-heading,
.claim-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}
.claim-progress-heading > div,
.claim-section-heading > div {
  display: flex;
  flex-direction: column;
}
.claim-progress-heading span,
.claim-section-heading span {
  color: var(--claim-ink);
  font-size: 16px;
  font-weight: 700;
}
.claim-progress-heading small,
.claim-section-heading small {
  margin-top: 2px;
  color: var(--claim-muted);
}
.claim-status-chip {
  background: #e6f4f1 !important;
  color: #0d625b !important;
  font-weight: 700;
}
.claim-progress-scroll {
  overflow-x: auto;
  padding: 24px 5px 4px;
}
.claim-progress-track {
  display: grid;
  min-width: 680px;
  grid-template-columns: repeat(4, 1fr);
}
.claim-progress-step {
  position: relative;
  text-align: center;
}
.claim-progress-step:not(:last-child)::before {
  position: absolute;
  top: 16px;
  left: calc(50% + 18px);
  width: calc(100% - 36px);
  height: 3px;
  background: #dce3e8;
  content: "";
}
.claim-progress-step.is-complete:not(:last-child)::before {
  background: var(--claim-teal);
}
.claim-step-marker {
  position: relative;
  z-index: 1;
  display: grid;
  width: 34px;
  height: 34px;
  margin: 0 auto 8px;
  place-items: center;
  border: 3px solid #dce3e8;
  border-radius: 50%;
  background: #fff;
  color: #8290a3;
  font-weight: 700;
}
.is-complete .claim-step-marker,
.is-active .claim-step-marker {
  border-color: var(--claim-teal);
  background: var(--claim-teal);
  color: #fff;
}
.is-active .claim-step-marker {
  box-shadow: 0 0 0 6px rgba(20, 122, 112, 0.13);
}
.claim-step-label {
  color: var(--claim-ink);
  font-size: 11px;
  font-weight: 700;
}
.claim-step-meta {
  margin-top: 3px;
  color: var(--claim-muted);
  font-size: 10px;
}
.claim-info-card {
  margin-top: 20px;
  overflow: hidden;
}
.inquiry-secondary-grid {
  display: grid;
  max-width: 1080px;
  margin: 0 auto;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  gap: 20px;
}
.secondary-card {
  width: 100%;
}
.document-list {
  padding: 8px 18px 16px;
}
.document-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px 2px;
  border-bottom: 1px solid #edf0f3;
}
.document-row > .v-icon {
  color: var(--claim-teal);
}
.document-row > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}
.document-row strong {
  overflow: hidden;
  color: #344158;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.document-row small {
  color: var(--claim-muted);
}
.secondary-empty {
  display: flex;
  min-height: 150px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: var(--claim-muted);
  font-size: 12px;
}
.secondary-empty .v-icon {
  color: #9ba8b8;
  font-size: 38px;
}
::v-deep .inquiry-subrogation-table th {
  color: #25345f !important;
  font-size: 11px !important;
  white-space: nowrap;
}
::v-deep .inquiry-subrogation-table td {
  color: #34405f;
  font-size: 11px !important;
  white-space: nowrap;
}
.claim-section-heading {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(220, 231, 235, 0.78);
  background: rgba(248, 252, 251, 0.52);
}
.claim-section-heading > strong {
  color: var(--claim-teal);
  font-size: 13px;
}
.claim-detail-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 10px 22px 20px;
  column-gap: 36px;
}
.claim-detail-row {
  display: grid;
  grid-template-columns: minmax(115px, 0.7fr) minmax(0, 1.3fr);
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid #edf0f3;
}
.claim-detail-row span {
  color: var(--claim-muted);
  font-size: 12px;
}
.claim-detail-row strong {
  overflow-wrap: anywhere;
  color: #344158;
  font-size: 12px;
  font-weight: 600;
}
.inquiry-empty-state {
  margin-top: 20px;
  padding: 55px 24px;
  text-align: center;
}
.inquiry-empty-state .v-icon {
  color: #9ba8b8;
  font-size: 58px;
}
.inquiry-empty-state h3 {
  margin: 12px 0 4px;
  color: var(--claim-ink);
}
.inquiry-empty-state p {
  margin: 0;
  color: var(--claim-muted);
}
@media (max-width: 700px) {
  .claim-inquiry {
    padding: 14px;
  }
  .inquiry-search-card {
    padding: 18px;
  }
  .inquiry-form,
  .claim-detail-list {
    grid-template-columns: 1fr;
  }
  .inquiry-form {
    padding: 6px;
  }
  .inquiry-secondary-grid {
    grid-template-columns: 1fr;
  }
  .inquiry-search-btn {
    width: 100%;
  }
  .claim-detail-list {
    padding: 8px 16px 16px;
  }
  .claim-progress-heading,
  .claim-section-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
@media (prefers-reduced-motion: reduce) {
  .inquiry-search-btn {
    transition: none !important;
  }
  .inquiry-search-btn::after {
    display: none;
  }
}
</style>
